import React, { useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, ChevronDown, Search, Send, ShieldAlert, X, Loader2 } from 'lucide-react';
import { money, render, segments } from './api';

export const ReviewStep = ({ resolved, removed, setRemoved, body, marketing, when, setWhen, at, setAt }) => {
  const [q, setQ] = useState('');
  const [showEx, setShowEx] = useState(false);
  const all = useMemo(() => [...(resolved?.recipients || []), ...(resolved?.removed || [])].sort((a, b) => a.name.localeCompare(b.name)), [resolved]);
  const list = all.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || r.phone.includes(q.replace(/\s/g, '')));
  const toggle = (p) => setRemoved((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });
  if (!resolved) return <div className="bsx-step bsx-empty"><Loader2 size={18} className="animate-spin" /></div>;
  return (
    <div className="bsx-step" data-testid="bsx-step-review">
      <div className="bsx-review-head">
        <div className="bsx-search"><Search size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipients" data-testid="bsx-review-search" /></div>
        <span className="bsx-muted">{all.length - removed.size} of {all.length} selected{resolved.duplicates_removed ? ` · ${resolved.duplicates_removed} duplicate${resolved.duplicates_removed === 1 ? '' : 's'} merged` : ''}</span>
      </div>
      <ul className="bsx-table" data-testid="bsx-recipient-list">
        {list.slice(0, 400).map((r) => { const on = !removed.has(r.phone); return (
          <li key={r.phone} className={on ? '' : 'off'}>
            <label><input type="checkbox" checked={on} onChange={() => toggle(r.phone)} data-testid={`bsx-recipient-${r.phone}`} />
              <span className="bsx-av">{(r.first_name || r.name || '?')[0]}</span>
              <span className="bsx-row-body"><b>{r.name}</b><small>{r.phone} · {r.via.join(', ')}</small></span>
              <em className="bsx-tag">{r.kind === 'contact' ? r.status : r.kind === 'lead' ? r.source : 'Manual'}</em>
              <span className="bsx-segs num">{segments(render(body, r, marketing))} SMS</span>
            </label>
          </li>
        ); })}
        {list.length > 400 && <li className="bsx-empty">Showing the first 400. Search to find others.</li>}
        {!list.length && <li className="bsx-empty">No recipients match</li>}
      </ul>
      {resolved.excluded.length > 0 && (
        <div className="bsx-excl">
          <button type="button" onClick={() => setShowEx((s) => !s)} data-testid="bsx-excluded-toggle"><ShieldAlert size={14} />{resolved.excluded.length} excluded automatically (no valid mobile or opted out)<ChevronDown size={14} className={showEx ? 'rot' : ''} /></button>
          {showEx && <ul data-testid="bsx-excluded-list">{resolved.excluded.slice(0, 300).map((r, i) => <li key={`${r.id}-${i}`}><b>{r.name}</b><span>{r.reason}</span></li>)}</ul>}
        </div>
      )}
      <div className="bsx-when" role="radiogroup">
        <button type="button" className={when === 'now' ? 'on' : ''} onClick={() => setWhen('now')} data-testid="bsx-when-now"><Send size={15} /><span><b>Send now</b><small>Starts straight after you confirm</small></span></button>
        <button type="button" className={when === 'later' ? 'on' : ''} onClick={() => setWhen('later')} data-testid="bsx-when-later"><CalendarClock size={15} /><span><b>Schedule for later</b><small>Hobart time</small></span></button>
      </div>
      {when === 'later' && <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="bsx-dt" data-testid="bsx-schedule-at" />}
    </div>
  );
};

// Final guard before a bulk send: restate who, how many, cost, and require an explicit tick.
export const ConfirmSend = ({ count, parts, cost, when, at, busy, onClose, onSend }) => {
  const [ok, setOk] = useState(false);
  return (
    <div className="bsx-modal" onClick={onClose} data-testid="bsx-confirm">
      <div className="bsx-modal-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="bsx-x" onClick={onClose} aria-label="Close"><X size={16} /></button>
        <span className="bsx-modal-ic"><AlertTriangle size={20} /></span>
        <h3>{when === 'later' ? 'Schedule' : 'Send'} to {count} {count === 1 ? 'person' : 'people'}?</h3>
        <p>{parts} SMS parts · about <b>{money(parts * cost)}</b>{when === 'later' && at ? ` · ${at.replace('T', ' at ')} Hobart time` : ''}. Texts can’t be recalled once they go out.</p>
        <label className="bsx-check"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="bsx-confirm-check" />I’ve checked the recipients and the message</label>
        <div className="bsx-modal-btns">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Back</button>
          <button type="button" className="btn btn-solid" disabled={!ok || busy} onClick={onSend} data-testid="bsx-confirm-send">{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}{when === 'later' ? 'Schedule' : `Send ${count} SMS`}</button>
        </div>
      </div>
    </div>
  );
};
